import * as React from 'react';
import {withStyles, WithStyles, IconButton, CircularProgress} from '@material-ui/core';
import {connect} from 'react-redux';
import TranslateNew from '@translate/TranslateNew';
import { ChatStyle } from './Chat.style';
import AddIcon from "@material-ui/icons/AddRounded";
import FileIcon from '@material-ui/icons/AttachFileRounded';
import CameraIcon from "@material-ui/icons/CameraAltOutlined";
import PdfIcon from "@material-ui/icons/PictureAsPdfRounded";
import DocumentIcon from "@material-ui/icons/DescriptionRounded";
import SpreadsheetIcon from "@material-ui/icons/TableChartRounded";
import VideoIcon from "@material-ui/icons/MovieRounded";
import ArchiveIcon from "@material-ui/icons/ArchiveRounded";
import GenericFileIcon from "@material-ui/icons/InsertDriveFileRounded";
import CloseIcon from "@material-ui/icons/CloseRounded";
import PinIcon from "@material-ui/icons/LocationOnRounded";
import SharedIconButton from '@components/common/iconButton/iconButton.component';
import * as ChatActions from '@services/chat/chat.actions';
import { default as SendIcon } from '@material-ui/icons/Send';
import { primaryColor, styleColors } from 'src/main.style';
import { handleBlobUpload, handleImageUpload, isImageFile, mimeFromFilename } from '@helpers/uploadHelper';
import Autosize from './Autosize';
import * as FileActions from '@services/file/file.actions';
import { IUploadFile, IStuffBeingUploaded, IFinishedUpload, IStuffToUpload } from '@app-types/websocket/file.types';
import { getPicture } from '@helpers/cameraHelper';
import CordovaContext from 'src/cordovaContext';
import { ChatType, IChatCheckIn, IChatMessage, ICreateChatMessage, ILocallyStoredMsg } from '@app-types/websocket/chat.types';
import GifIcon from '@material-ui/icons/Gif';
import Giphy from './giphy/Giphy';
import ShareLocationDialog from './ShareLocationDialog';
import { IDeviceLocation } from '@helpers/locationHelper';
import { displayErrorMessage } from '@services/user/user.actions';
import Button from '@components/common/button/Button';
import WandIcon from '@components/svg/wandIcon';
import { AI_RESULT, AI_TASK_TYPE, IAiTask } from '@app-types/websocket/event.types';
import * as EventActions from '@services/events/event.action';
import { replyPreviewText } from '@services/chat/chatReply';


const tn: TranslateNew = TranslateNew.getInstance();
const MAX_CHAT_VIDEO_SIZE_BYTES = 300 * 1024 * 1024;
const MAX_CHAT_ATTACHMENTS = 5;
const MAX_CHAT_NON_IMAGE_ATTACHMENTS = 3;
const CHAT_DRAFT_SESSION_ID = Date.now();
// The polish button shows for messages longer than this, and a polish that takes longer than the timeout has failed.
const POLISH_MIN_LENGTH = 20;
const POLISH_TIMEOUT_MS = 10000;
// Polish request ids count down from -1: negative, so they never match a stored AI task, and they must fit the
// backend's int id. They only need to be unique within this page, since answers go back to this session only.
let nextPolishId = -1;
// The latest messages sent along as context, so the backend does not have to load them.
const POLISH_CONTEXT_MESSAGES = 3;
const POLISH_CONTEXT_MESSAGE_LENGTH = 500;

interface IFileTypeVisual {
  Icon: React.ComponentType<{ style?: React.CSSProperties }>;
  color: string;
}

function getFileExtension(fileName: string | undefined): string | undefined {
  const parts = (fileName || "").split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : undefined;
}

function getFileTypeVisual(fileName: string | undefined): IFileTypeVisual {
  switch (getFileExtension(fileName)) {
    case "pdf":
      return { Icon: PdfIcon, color: styleColors.materialRed };
    case "doc":
    case "docx":
    case "txt":
    case "rtf":
    case "md":
    case "ppt":
    case "pptx":
      return { Icon: DocumentIcon, color: styleColors.actionBlue };
    case "xls":
    case "xlsx":
    case "csv":
      return { Icon: SpreadsheetIcon, color: styleColors.successGreen };
    case "mp4":
    case "mov":
      return { Icon: VideoIcon, color: styleColors.vividPurple };
    case "zip":
      return { Icon: ArchiveIcon, color: styleColors.mutedMauve };
    default:
      return { Icon: GenericFileIcon, color: styleColors.greyMedium3 };
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface IProps extends WithStyles<typeof ChatStyle> {
  isAuthenticated:boolean;
  chatId:number|undefined;
  uploadFile: (file: IUploadFile) => void;
  uploadingFiles: IStuffBeingUploaded[];
  pendingUploads: IStuffToUpload[];
  uploadedFiles: IFinishedUpload[];
  uploadedFileUsed: (fileId: number) => void;
  sendChatMessage: (data: ICreateChatMessage) => void;
  sendChatMessageToxicChecked: (data: ICreateChatMessage) => void;
  chatCheckIn: (data: IChatCheckIn) => void;
  isSupportChat: boolean;
  displayErrorMessage: (msg: string) => void;
  hideFAB:boolean;
  savedMessage: string;
  clearSavedMessage: () => void;
  replyToId?: number; // the message the next send replies to
  clearReplyTo: () => void;
  locallySaveChatMessage: (msg: ILocallyStoredMsg) => void;
  localChatHistory: ILocallyStoredMsg[];
  polishTasks: IAiTask[];
  aiTask: (task:IAiTask) => void;
  userId: number;
  chatMessages: IChatMessage[];
}

interface IState {
  chatMessage:string;
  showSubmenu:boolean,
  attachFiles:number[];
  internalIds:number[];
  showGiphy: boolean;
  checkInChatId: number|undefined; // the chat the open share-location dialog posts to
  polishStatus: "IDLE" | "WORKING" | "FAILED";
  polishId: number|undefined; // the id of the polish request we are waiting for
}

class ChatBottomBar extends React.Component<IProps, IState> {
  private timer:number|undefined;
  private draftSaveGeneration:number = 0;
  private polishTimer:number|undefined;
  private inputRef = React.createRef<Autosize>();

  constructor(props: IProps) {
    super(props);
    this.state = {
      showSubmenu:false,
      chatMessage:props.savedMessage || "",
      attachFiles:[],
      internalIds:[],
      showGiphy: false,
      checkInChatId: undefined,
      polishStatus: "IDLE",
      polishId: undefined,
    };
  }

  public componentDidMount() {
    if (this.props.chatId !== undefined) {
        this.loadStoredMessage(this.getStoredMessage(this.props.chatId));
    }
  }

  public componentWillUnmount(): void {
    this.cancelPendingDraftSave();
    this.clearPolishTimer();
  }

  public componentDidUpdate(oldprops:IProps) {
    if (oldprops.chatId !== this.props.chatId && this.state.polishStatus !== "IDLE") {
      // A late result belongs to the draft of the chat we left.
      this.resetPolish();
    } else if (this.props.polishTasks !== oldprops.polishTasks && this.state.polishStatus === "WORKING") {
      this.handlePolishResult();
    }
    if (this.props.savedMessage !== oldprops.savedMessage && this.props.savedMessage.length > 0) {
      this.loadStoredMessage(this.props.savedMessage);
    } else if (oldprops.chatId !== this.props.chatId) {
      if (oldprops.chatId !== undefined) {
        this.saveDraft(oldprops.chatId, this.state.chatMessage, this.state.internalIds, this.state.attachFiles);
      }
      if (this.props.chatId !== undefined) {
        this.loadStoredMessage(this.getStoredMessage(this.props.chatId));
      } else {
        this.loadStoredMessage("");
      }

      
    }
    if (this.props.replyToId !== undefined && this.props.replyToId !== oldprops.replyToId
      && this.props.chatId === oldprops.chatId && this.inputRef.current) {
      // Picking "Reply" on a message goes straight on to writing the reply.
      this.inputRef.current.focus();
    }
    if (oldprops.chatId !== this.props.chatId && this.state.checkInChatId !== undefined) {
      // Never post a check-in to a chat the user is no longer looking at.
      this.setState({checkInChatId: undefined});
    }
    const fileStateChanged = this.props.uploadedFiles !== oldprops.uploadedFiles
      || this.props.uploadingFiles !== oldprops.uploadingFiles
      || this.props.pendingUploads !== oldprops.pendingUploads;
    if (fileStateChanged) {
      let internalIdsNew = this.state.internalIds.slice();
      const attachFilesNew = this.state.attachFiles.slice();
      let stateChanged = false;
      let removedAttachment = false;

      if (this.props.uploadingFiles) {
        for (const uf of this.props.uploadingFiles) {
          if (internalIdsNew.includes(uf.internalId)) {
            internalIdsNew = internalIdsNew.filter((i) => i !== uf.internalId);
            if (!attachFilesNew.includes(uf.id)) {
              attachFilesNew.push(uf.id);
              stateChanged = true;
            }
            stateChanged = true;
          }
        }
      }

      if (this.props.uploadedFiles) {
        for (const uf of this.props.uploadedFiles) {
          if (internalIdsNew.includes(uf.internalId)) {
            internalIdsNew = internalIdsNew.filter((i) => i !== uf.internalId);
            if (!attachFilesNew.includes(uf.id)) {
              attachFilesNew.push(uf.id);
              stateChanged = true;
            }
            stateChanged = true;
          }
        }
      }

      if (internalIdsNew.length > 0) {
        const pendingInternalIds = new Set<number>();
        for (const file of this.props.pendingUploads) {
          pendingInternalIds.add(file.internalId);
        }
        for (const file of this.props.uploadingFiles) {
          pendingInternalIds.add(file.internalId);
        }
        for (const file of this.props.uploadedFiles) {
          pendingInternalIds.add(file.internalId);
        }

        const beforeCount = internalIdsNew.length;
        internalIdsNew = internalIdsNew.filter((id) => pendingInternalIds.has(id));
        if (internalIdsNew.length !== beforeCount) {
          stateChanged = true;
          removedAttachment = true;
        }
      }

      if (attachFilesNew.length > 0) {
        const activeFileIds = new Set<number>();
        for (const file of this.props.uploadingFiles) {
          activeFileIds.add(file.id);
        }
        for (const file of this.props.uploadedFiles) {
          activeFileIds.add(file.id);
        }

        const beforeCount = attachFilesNew.length;
        const filtered = attachFilesNew.filter((id) => activeFileIds.has(id));
        if (filtered.length !== beforeCount) {
          attachFilesNew.splice(0, attachFilesNew.length, ...filtered);
          stateChanged = true;
          removedAttachment = true;
        }
      }

      if (stateChanged) {
        this.setState({internalIds:internalIdsNew, attachFiles:attachFilesNew}, () => this.saveDraft());
      }
      if (removedAttachment) {
        this.props.displayErrorMessage("One attachment failed to upload and was removed.");
      }
    }
  }

  public render() {
    const {classes, chatId, isAuthenticated, hideFAB} = this.props;
    const {chatMessage, showSubmenu, attachFiles, polishStatus} = this.state;

    let FABstyle:string = classes.FAB + " " +classes.FABLeft;

    if (!this.state.showSubmenu) {
      FABstyle=FABstyle+" "+classes.FABHidden;
    }

    let disableSend:boolean = (chatId === undefined || !isAuthenticated || polishStatus === "WORKING"
      || (chatMessage.trim() === "" && attachFiles.length === 0 && this.state.internalIds.length === 0));

    const files:JSX.Element[] = [];

    const disableAddMore = !this.canAddMoreAttachments();

    for (const internalId of this.state.internalIds) {
      files.push(this.generatePendingFileRender(internalId));
    }

    for (const fileId of this.state.attachFiles){
      files.push(this.generateFileRender(fileId));
    }

    if (attachFiles.length > 0) {
      const allAttachmentsUploaded = attachFiles.every((fileId) => this.props.uploadedFiles.some((f) => f.id === fileId));
      if (!allAttachmentsUploaded) {
        disableSend = true;
      }
    }
    if (this.state.internalIds.length > 0) {
      disableSend = true;
    }

    const isCordovaApp = this.context;

    let cameraButton:JSX.Element|undefined;
    let giphyButton:JSX.Element|undefined;
    let attachButton:JSX.Element|undefined;
    if (isCordovaApp) {
      giphyButton = 
        <div className={FABstyle} style={{bottom:this.state.showSubmenu?'264px':'20px'}} 
          onClick={() => this.setState({ showGiphy: true, showSubmenu: false })}>
          <GifIcon className={classes.greyIcon} /> 
        </div>
      cameraButton = 
        <div className={FABstyle} style={{bottom:this.state.showSubmenu?'182px':'20px'}} onClick={() => this.loadImage("CAMERA")}>
          <CameraIcon className={classes.greyIcon}/>
        </div>
      attachButton = <div className={FABstyle} style={{bottom:this.state.showSubmenu?'100px':'20px'}}>
        <label htmlFor="add-file-5">
          <input type="file"  multiple={true} style = {{opacity: 0, position: 'absolute'}}
            id={"add-file-5"} onChange={this.loadLocalFile} />
          <FileIcon className={classes.greyIcon}/>
        </label>
      </div>
    } else {
      giphyButton = 
        <div className={FABstyle} style={{bottom:this.state.showSubmenu ? '182px' : '20px'}}
          onClick={() => this.setState({ showGiphy: true, showSubmenu: false })}>
          <GifIcon className={classes.greyIcon} /> 
        </div>
      attachButton = <div className={FABstyle} style={{bottom:this.state.showSubmenu?'100px':'20px'}}>
        <label htmlFor="add-file-5">
          <input type="file"  multiple={true} style = {{opacity: 0, position: 'absolute'}}
            id={"add-file-5"} onChange={this.loadLocalFile} />
          <FileIcon className={classes.greyIcon}/>
        </label>
        </div>
    }

    // Topmost, so the older buttons keep their places. Sender 0 in the support chat is the support team.
    let checkInButton:JSX.Element|undefined;
    if (chatId !== undefined && !this.props.isSupportChat) {
      checkInButton =
        <SharedIconButton className={FABstyle + " " + classes.FABButton} aria-label={tn.lang.chatCheckIn}
          style={{bottom:this.state.showSubmenu ? (isCordovaApp ? '346px' : '264px') : '20px'}}
          onClick={() => this.setState({checkInChatId: chatId, showSubmenu: false})}>
          <PinIcon className={classes.greyIcon} />
        </SharedIconButton>
    }

    return (
      <div className={classes.chatBottomBar} >
        <IconButton size="small" style={{minHeight: '40px'}} disabled={disableAddMore} 
          onClick={() => this.setState({showSubmenu:!showSubmenu})}>
          <AddIcon />
        </IconButton>
        <div className={classes.chatInputArea}>
          {this.renderPolishButton()}
          <div className={classes.chatInputBox}>
            {this.renderReplyBar()}
            <div className={classes.chatFiles}>
              {files}
            </div>
            <Autosize value={chatMessage} ref={this.inputRef}
              readOnly={polishStatus === "WORKING"}
              handleKeyPress={(evt) => this.handleKeyPress(evt)}
              onPaste={(evt) => this.handlePaste(evt)}
              onChange={(evt) => this.handleMessageChange(evt.target.value)}/>
          </div>
        </div>
        <IconButton style={{backgroundColor:(disableSend?"#CCCCCC":primaryColor)}} disabled={disableSend} onClick={() => this.handleSendMessage()}>
          <SendIcon style={{fill: "#FFFFFF", fontSize:'16px'}} />
        </IconButton>

        {attachButton}
        {giphyButton}
        {cameraButton}
        {checkInButton}

        <Giphy
          isOpen={this.state.showGiphy} 
          onClose={() => this.setState({ showGiphy: false })}
          onGifSelect={(gifUrl) => this.handleGifSelected(gifUrl)}
        />
        <ShareLocationDialog
          open={this.state.checkInChatId !== undefined}
          onClose={() => this.setState({checkInChatId: undefined})}
          onShare={(location) => this.handleCheckIn(location)}
        />

      </div>
    );
  }

  private handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const { key, ctrlKey } = event;
    if (ctrlKey && key === 'Enter' && this.state.polishStatus !== "WORKING") {
      this.handleSendMessage();
    }
  }

  private handleMessageChange(msg: string) {
    this.setState({chatMessage: msg});
    if (this.state.polishStatus === "FAILED") {
      this.resetPolish();
    }
    this.storeMsg(msg);
  }

  private renderReplyBar(): JSX.Element|undefined {
    const {classes, replyToId} = this.props;
    if (replyToId === undefined) {
      return;
    }
    const preview = replyPreviewText(this.props.chatMessages.find((m) => m && m.id === replyToId));
    return (
      <div className={classes.chatReplyQuote + " " + classes.chatReplyBar}>
        <span className={classes.chatReplyLabel}>{tn.lang.chatReplyingTo}</span>
        <span className={classes.chatReplyText}>{preview !== "" ? preview : tn.lang.chatReplyToEarlierMessage}</span>
        <SharedIconButton className={classes.chatReplyClose} size="small" aria-label={tn.lang.chatCancelReply}
          onClick={() => this.props.clearReplyTo()}>
          <CloseIcon style={{fontSize: '16px'}}/>
        </SharedIconButton>
      </div>
    );
  }

  private renderPolishButton(): JSX.Element|undefined {
    const {classes, chatId, isAuthenticated} = this.props;
    const {polishStatus} = this.state;
    if (chatId === undefined || (polishStatus === "IDLE" && this.state.chatMessage.trim().length <= POLISH_MIN_LENGTH)) {
      return;
    }

    let content: JSX.Element;
    if (polishStatus === "WORKING") {
      content = <span className={classes.polishWorking}>{tn.lang.chatPolishing}</span>;
    } else if (polishStatus === "FAILED") {
      content = <span className={classes.polishFailed}>{tn.lang.chatPolishFailed}</span>;
    } else {
      content = <span>{tn.lang.chatPolish}</span>;
    }

    return (
      <Button className={classes.polishButton} disabled={polishStatus === "WORKING" || !isAuthenticated}
        startIcon={polishStatus === "WORKING" ? undefined : <WandIcon style={{fontSize: '18px'}} />}
        onClick={() => this.handlePolish()}>
        {content}
      </Button>
    );
  }

  private handlePolish() {
    const msg = this.state.chatMessage.trim();
    if (this.state.polishStatus === "WORKING" || msg.length <= POLISH_MIN_LENGTH) {
      return;
    }
    const polishId = nextPolishId--;
    this.clearPolishTimer();
    this.polishTimer = window.setTimeout(() => {
      this.polishTimer = undefined;
      if (this.state.polishStatus === "WORKING" && this.state.polishId === polishId) {
        this.setState({polishStatus: "FAILED", polishId: undefined});
      }
    }, POLISH_TIMEOUT_MS);
    this.setState({polishStatus: "WORKING", polishId});
    this.props.aiTask({
      id: polishId,
      taskType: AI_TASK_TYPE.MESSAGE_POLISH,
      promptOne: msg,
      promptTwo: JSON.stringify(this.getPolishContext()),
    });
  }

  // The last messages people wrote in the chat (not system messages or calls), oldest first.
  private getPolishContext(): Array<{fromMe: boolean, text: string}> {
    return this.props.chatMessages
      .filter((m) => m && m.senderId > 0 && !m.callId && typeof m.msg === "string" && m.msg.trim().length > 0)
      .sort((a, b) => a.id - b.id)
      .slice(-POLISH_CONTEXT_MESSAGES)
      .map((m) => ({fromMe: m.senderId === this.props.userId, text: m.msg.trim().slice(0, POLISH_CONTEXT_MESSAGE_LENGTH)}));
  }

  private handlePolishResult() {
    const task = this.props.polishTasks.find((t) => t.id === this.state.polishId);
    if (!task || task.status === AI_RESULT.IN_PROGRESS) {
      return;
    }
    this.clearPolishTimer();
    if (task.status === AI_RESULT.SUCCESS && task.result && task.result.trim().length > 0) {
      const polished = task.result.trim();
      this.setState({chatMessage: polished, polishStatus: "IDLE", polishId: undefined});
      this.storeMsg(polished);
    } else {
      this.setState({polishStatus: "FAILED", polishId: undefined});
    }
  }

  private resetPolish() {
    this.clearPolishTimer();
    this.setState({polishStatus: "IDLE", polishId: undefined});
  }

  private clearPolishTimer() {
    if (this.polishTimer !== undefined) {
      window.clearTimeout(this.polishTimer);
      this.polishTimer = undefined;
    }
  }

  private getStoredMessage(chatId: number): ILocallyStoredMsg | undefined {
    return this.props.localChatHistory.find((msg) => msg && msg.chatId === chatId);
  }

  private activeInternalIds(): Set<number> {
    const ids = new Set<number>();
    for (const file of this.props.pendingUploads) {
      ids.add(file.internalId);
    }
    for (const file of this.props.uploadingFiles) {
      ids.add(file.internalId);
    }
    for (const file of this.props.uploadedFiles) {
      ids.add(file.internalId);
    }
    return ids;
  }

  private activeAttachmentIds(): Set<number> {
    const ids = new Set<number>();
    for (const file of this.props.uploadingFiles) {
      ids.add(file.id);
    }
    for (const file of this.props.uploadedFiles) {
      ids.add(file.id);
    }
    return ids;
  }

  private normalizeNumberList(items: number[] | undefined, allowedIds?: Set<number>): number[] {
    if (!Array.isArray(items)) {
      return [];
    }

    const result:number[] = [];
    for (const item of items) {
      if (typeof item === "number" && Number.isFinite(item) && !result.includes(item)
        && (!allowedIds || allowedIds.has(item))) {
        result.push(item);
      }
    }
    return result;
  }

  private loadStoredMessage(storedMessage:string | ILocallyStoredMsg | undefined) {
    const msg = typeof storedMessage === "string" ? storedMessage : (storedMessage?.msg || "");
    const keepSessionInternalIds = typeof storedMessage !== "string"
      && storedMessage?.uploadDraftSessionId === CHAT_DRAFT_SESSION_ID;
    const internalIds = typeof storedMessage === "string"
      ? []
      : this.normalizeNumberList(storedMessage?.uploadInternalIds, keepSessionInternalIds ? undefined : this.activeInternalIds());
    const attachFiles = typeof storedMessage === "string"
      ? []
      : this.normalizeNumberList(storedMessage?.attachmentIds, this.activeAttachmentIds());

    this.setState({showSubmenu:false, chatMessage:msg, attachFiles, internalIds});
  }

  private saveDraft(
    chatId: number | undefined = this.props.chatId,
    msg: string = this.state.chatMessage,
    internalIds: number[] = this.state.internalIds,
    attachFiles: number[] = this.state.attachFiles,
  ) {
    if (chatId === undefined) {
      return;
    }
    this.props.locallySaveChatMessage({
      chatId,
      msg,
      uploadInternalIds: this.normalizeNumberList(internalIds),
      attachmentIds: this.normalizeNumberList(attachFiles),
      uploadDraftSessionId: CHAT_DRAFT_SESSION_ID,
    });
  }

  private getAttachmentNames(): Array<string | undefined> {
    const names:Array<string | undefined> = [];
    for (const internalId of this.state.internalIds) {
      const file = this.props.uploadedFiles.find((f) => f.internalId === internalId)
        || this.props.uploadingFiles.find((f) => f.internalId === internalId);
      names.push(file ? file.fileName : this.props.pendingUploads.find((f) => f.internalId === internalId)?.name);
    }
    for (const fileId of this.state.attachFiles) {
      const file = this.props.uploadedFiles.find((f) => f.id === fileId)
        || this.props.uploadingFiles.find((f) => f.id === fileId);
      names.push(file?.fileName);
    }
    return names;
  }

  private getRemainingAttachmentSlots(): { total: number; nonImage: number } {
    const names = this.getAttachmentNames();
    const nonImageCount = names.filter((name) => !isImageFile(name)).length;
    return {
      total: MAX_CHAT_ATTACHMENTS - names.length,
      nonImage: MAX_CHAT_NON_IMAGE_ATTACHMENTS - nonImageCount,
    };
  }

  private canAddMoreAttachments(): boolean {
    if (this.getRemainingAttachmentSlots().total <= 0) {
      return false;
    }

    if (this.state.internalIds.length > 0) {
      return false;
    }

    for (const fileId of this.state.attachFiles) {
      const upload = this.props.uploadingFiles.find((f) => f.id === fileId);
      if (upload && (upload.loaded < 100 || upload.loaded === undefined)) {
        return false;
      }
    }

    return true;
  }

  private getAttachmentLimitMessage(): string {
    return `You can add up to ${MAX_CHAT_ATTACHMENTS} attachments, of which at most ${MAX_CHAT_NON_IMAGE_ATTACHMENTS} can be non-image files.`;
  }

  private getCannotAddAttachmentMessage(): string {
    if (this.getRemainingAttachmentSlots().total <= 0) {
      return this.getAttachmentLimitMessage();
    }
    return "Please wait for the current attachment to finish uploading.";
  }

  private loadLocalFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    try {
      const selectedFiles = Array.from(input.files || []);
      if (selectedFiles.length === 0) {
        this.setState({ showSubmenu: false });
        return;
      }
      if (!this.canAddMoreAttachments()) {
        this.props.displayErrorMessage(this.getCannotAddAttachmentMessage());
        this.setState({ showSubmenu: false });
        return;
      }
      this.addAttachmentsFromFiles(selectedFiles);

    } catch (e) {
      this.props.displayErrorMessage("Attachment upload failed.");
      this.setState({ showSubmenu: false });
    } finally {
      // allow selecting the same file again
      try { input.value = ""; } catch { /* ignore */ }
    }
  };

  private handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const file = this.getFileFromClipboard(event.clipboardData);
    if (!file) {
      return;
    }

    event.preventDefault();

    if (!this.canAddMoreAttachments()) {
      this.props.displayErrorMessage(this.getCannotAddAttachmentMessage());
      return;
    }

    try {
      this.addAttachmentsFromFiles([file]);
    } catch (e) {
      this.props.displayErrorMessage("Attachment upload failed.");
      this.setState({ showSubmenu: false });
    }
  }

  private getFileFromClipboard(clipboardData: DataTransfer): File | undefined {
    if (clipboardData.files && clipboardData.files.length > 0) {
      const file = clipboardData.files.item(0);
      if (file) {
        return file;
      }
    }

    if (!clipboardData.items) {
      return;
    }

    for (const item of Array.from(clipboardData.items)) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          return file;
        }
      }
    }

    return;
  }

  private addAttachmentsFromFiles(files: File[]) {
    const slots = this.getRemainingAttachmentSlots();
    const internalIds = [...this.state.internalIds];
    let skippedOverLimit = false;

    for (const file of files) {
      const fileName = this.getAttachmentFileName(file);
      const countsAsImage = isImageFile(fileName);
      if (slots.total <= 0 || (!countsAsImage && slots.nonImage <= 0)) {
        skippedOverLimit = true;
        continue;
      }

      const internalId = this.startAttachmentUpload(file, fileName);
      if (internalId === undefined) {
        continue;
      }
      internalIds.push(internalId);
      slots.total -= 1;
      if (!countsAsImage) {
        slots.nonImage -= 1;
      }
    }

    if (skippedOverLimit) {
      this.props.displayErrorMessage(this.getAttachmentLimitMessage());
    }

    if (internalIds.length !== this.state.internalIds.length) {
      this.saveDraft(undefined, this.state.chatMessage, internalIds, this.state.attachFiles);
    }
    this.setState({
      internalIds,
      showSubmenu: false,
    });
  }

  private startAttachmentUpload(file: File, fileName: string): number | undefined {
    const mime = (file.type && file.type !== "application/octet-stream" ? file.type : mimeFromFilename(fileName)).toLowerCase();
    const isImage = mime.startsWith("image/");
    const isVideo = mime.startsWith("video/");

    if (isVideo && file.size > MAX_CHAT_VIDEO_SIZE_BYTES) {
      this.props.displayErrorMessage("Video files larger than 300 MB are not supported.");
      return;
    }

    const uploadBlob = file.slice(0, file.size, mime || undefined);
    if (isImage) {
      const previewUrl = URL.createObjectURL(uploadBlob);
      return handleImageUpload(uploadBlob, fileName, previewUrl, this.props.uploadFile).internalId;
    }
    return handleBlobUpload(uploadBlob, fileName, this.props.uploadFile).internalId;
  }

  private getAttachmentFileName(file: File): string {
    if (file.name && file.name.trim().length > 0) {
      return file.name.trim();
    }

    const mime = (file.type || "").toLowerCase();
    const extension = this.extensionFromMime(mime);
    if (mime.startsWith("image/")) {
      return `pasted-image.${extension || "png"}`;
    }

    return extension ? `pasted-file.${extension}` : "pasted-file";
  }

  private extensionFromMime(mime: string): string | undefined {
    switch (mime) {
      case "image/jpeg":
      case "image/jpg":
        return "jpg";
      case "image/png":
        return "png";
      case "image/gif":
        return "gif";
      case "image/webp":
        return "webp";
      case "image/bmp":
        return "bmp";
      case "image/svg+xml":
        return "svg";
      case "application/pdf":
        return "pdf";
      case "text/plain":
        return "txt";
      case "video/mp4":
        return "mp4";
      case "video/quicktime":
        return "mov";
      default:
        return;
    }
  }

  private loadImage(source:"CAMERA") {
    getPicture(source, (data) => this.imageLoadedFromCamera(data), (error) => this.imageFailed(error));
    this.setState({showSubmenu:false});
  }

  private imageLoadedFromCamera(blob: Blob) {
    const mime = "image/jpeg";
    const typedBlob = blob.slice(0, blob.size, mime);
    const previewUrl = URL.createObjectURL(typedBlob);
    const fileUpload = handleImageUpload(typedBlob, "chat.jpeg", previewUrl, this.props.uploadFile);

    const internalIds = [...this.state.internalIds, fileUpload.internalId];
    this.saveDraft(undefined, this.state.chatMessage, internalIds, this.state.attachFiles);
    this.setState({
      internalIds,
      showSubmenu: false,
    });
  }

  private imageFailed(error:string) {
    console.log(error);
  }

  private removeFile(fileId:number) {
    // Revoke preview URL if we created one
    const uploading = this.props.uploadingFiles?.find(f => f.id === fileId);
    const uploaded = this.props.uploadedFiles?.find(f => f.id === fileId);
    const url = uploading?.localURL || uploaded?.localURL;

    if (url && url.startsWith("blob:")) {
      try { URL.revokeObjectURL(url); } catch { /* Ignore */ }
    }

    const attach = this.state.attachFiles.filter((f) => f !== fileId);
    this.setState({attachFiles:attach}, () => this.saveDraft());
    this.props.uploadedFileUsed(fileId);
  }

  private removePendingFile(internalId:number) {
    const internalIds = this.state.internalIds.filter((id) => id !== internalId);
    this.setState({internalIds}, () => this.saveDraft());
  }

  private generatePendingFileRender(internalId:number):JSX.Element {
    const uploaded = this.props.uploadedFiles.find((f) => f.internalId === internalId);
    if (uploaded) {
      const previewUrl = isImageFile(uploaded.fileName) ? (uploaded.permanentURL || uploaded.localURL) : undefined;
      return this.renderFile(uploaded.id, previewUrl, uploaded.fileName, 100, uploaded.fileSize);
    }

    const uploading = this.props.uploadingFiles.find((f) => f.internalId === internalId);
    if (uploading) {
      return this.renderFile(uploading.id, uploading.localURL, uploading.fileName, uploading.loaded, uploading.fileSize);
    }

    const pending = this.props.pendingUploads.find((f) => f.internalId === internalId);
    return this.renderAttachment("pending-" + internalId, () => this.removePendingFile(internalId),
      pending?.localURL, pending?.name, 0);
  }

  private generateFileRender(fileId:number):JSX.Element {
    const uploaded = this.props.uploadedFiles.find((f) => f.id === fileId);
    if (uploaded) {
      const previewUrl = isImageFile(uploaded.fileName) ? uploaded.permanentURL : undefined;
      return this.renderFile(fileId, previewUrl, uploaded.fileName, 100, uploaded.fileSize);
    }

    const uploading = this.props.uploadingFiles.find((f) => f.id === fileId);
    if (uploading) {
      return this.renderFile(fileId, uploading.localURL, uploading.fileName, uploading.loaded, uploading.fileSize);
    }

    return this.renderFile(fileId, undefined, undefined, 100);
  }

  private renderFile(fileId:number, previewUrl:string|undefined, fileName:string|undefined, progress:number, fileSize?:number):JSX.Element {
    return this.renderAttachment("" + fileId, () => this.removeFile(fileId), previewUrl, fileName, progress, fileSize);
  }

  private renderAttachment(key:string, onRemove:() => void, previewUrl:string|undefined, fileName:string|undefined,
    progress:number, fileSize?:number):JSX.Element {
    const {classes} = this.props;
    const uploading = progress === undefined || progress < 100;

    if (previewUrl) {
      return (
        <div className={classes.chatfileContainer} onClick={onRemove} key={key}>
          <div className={classes.chatFileClose}><CloseIcon style={{fontSize: '14px'}}/></div>
          {uploading && (<div className={classes.chatFileOverLay}>
            <CircularProgress variant="determinate" value={progress || 0} />
          </div>)}
          <img className={classes.chatFileImage} src={previewUrl}/>
        </div>);
    }

    const {Icon, color} = getFileTypeVisual(fileName);
    const extension = getFileExtension(fileName);
    const meta = uploading
      ? `${progress || 0} %`
      : [extension ? extension.toUpperCase() : undefined, fileSize ? formatFileSize(fileSize) : undefined]
        .filter((part) => part !== undefined).join(" · ");

    return (
      <div className={classes.chatfileContainer} onClick={onRemove} key={key} title={fileName}>
        <div className={classes.chatFileClose}><CloseIcon style={{fontSize: '14px'}}/></div>
        <div className={classes.chatFileCard}>
          <div className={classes.chatFileTypeBadge} style={{backgroundColor: color}}>
            <Icon style={{fontSize: '22px'}}/>
          </div>
          <div className={classes.chatFileDetails}>
            <span className={classes.chatFileName}>{fileName || "Attachment"}</span>
            {meta && <span className={classes.chatFileMeta}>{meta}</span>}
          </div>
          {uploading && (
            <div className={classes.chatFileProgressTrack}>
              <div className={classes.chatFileProgressBar} style={{width: `${progress || 0}%`}}/>
            </div>
          )}
        </div>
      </div>);
  }

  private handleSendMessage = () => {
    const msg:string = this.state.chatMessage.trim();
    const chatId = this.props.chatId;
    if (chatId === undefined || (msg.length === 0 && this.state.attachFiles.length === 0)) {return;}
    if (this.state.internalIds.length > 0) {
      this.props.displayErrorMessage("Please wait for attachments to finish uploading.");
      return;
    }
    const allAttachmentsUploaded = this.state.attachFiles.every((fileId) => this.props.uploadedFiles.some((f) => f.id === fileId));
    if (!allAttachmentsUploaded) {
      this.props.displayErrorMessage("Please wait for attachments to finish uploading.");
      return;
    }

    this.cancelPendingDraftSave();
    // The message that carries the text is the reply.
    const questionId = this.props.replyToId;

    if (this.state.attachFiles.length > 0) {
      for (let q: number = 0; q < this.state.attachFiles.length; q += 1) {
        const fileId = this.state.attachFiles[q];
        const includeMsg = (this.state.attachFiles.length - 1 === q);
        setTimeout(() => { // TODO: remove this is hack to net send att messages at once
            this.sendMessage(chatId, includeMsg?msg:'', fileId, includeMsg?questionId:undefined);
        }, ((q*100)+1));
      }
    } else  {
      this.sendMessage(chatId, msg, undefined, questionId);
    }
    if (questionId !== undefined) {
      this.props.clearReplyTo();
    }

    this.props.clearSavedMessage();
    this.props.locallySaveChatMessage({
      chatId,
      msg: "",
      uploadInternalIds: [],
      attachmentIds: [],
      uploadDraftSessionId: CHAT_DRAFT_SESSION_ID,
    });
    this.setState({chatMessage:"", showSubmenu:false, attachFiles:[], internalIds:[]});
  }

  private sendMessage(chatId:number, msg:string, attachmentId?:number, questionId?:number) {
    const newMsg: ICreateChatMessage = {
      chatId,
      attachmentId,
      msg,
      questionId,
    };
    if (msg.length > 4) {
      this.props.sendChatMessageToxicChecked(newMsg);
    } else {  
      this.props.sendChatMessage(newMsg);
    }
    
    if (attachmentId) {
      this.props.uploadedFileUsed(attachmentId);
    }
  }

  private handleGifSelected = (gifUrl: string) => {
    const chatId = this.props.chatId;
    if (!chatId){
      return;
    } 
  
    this.props.sendChatMessage({
      chatId,
      msg: gifUrl,
      questionId: this.props.replyToId,
    });
    if (this.props.replyToId !== undefined) {
      this.props.clearReplyTo();
    }
    this.setState({ showGiphy: false });
  };

  private handleCheckIn = (location: IDeviceLocation) => {
    const chatId = this.state.checkInChatId;
    if (chatId !== undefined) {
      this.props.chatCheckIn({chatId, latitude: location.latitude, longitude: location.longitude});
    }
    this.setState({checkInChatId: undefined});
  };

  private storeMsg = (msg: string) => {

    this.cancelPendingDraftSave();
    const chatId = this.props.chatId;
    const saveGeneration = this.draftSaveGeneration;
    this.timer = window.setTimeout(() => {
      if (chatId !== undefined && saveGeneration === this.draftSaveGeneration) {
        this.saveDraft(chatId, msg);
      }
      if (saveGeneration === this.draftSaveGeneration) {
        this.timer = undefined;
      }
    }, 2000);
      
  }

  private cancelPendingDraftSave() {
    this.draftSaveGeneration += 1;
    if (this.timer !== undefined) {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}

ChatBottomBar.contextType = CordovaContext;

function mapStateToProps(state: any, props: {chatId: number|undefined}) {
  const userId = state?.user?.session?.data?.id ?? 0;
  const chats = Array.isArray(state?.chat?.chats) ? state.chat.chats : [];
  const chat = props.chatId !== undefined ? chats.find((c: any) => c && c.id === props.chatId) : undefined;

  return {
    isSupportChat: !!chat && chat.chatType === ChatType.SYSTEM_CHAT,
    userId,
    chatMessages: chat && Array.isArray(chat.messages) ? chat.messages : [],
    pendingUploads: Array.isArray(state?.file?.dataToUpload) ? state.file.dataToUpload : [],
    uploadingFiles: Array.isArray(state?.file?.uploadSlots) ? state.file.uploadSlots : [],
    uploadedFiles: Array.isArray(state?.file?.uploadedFiles) ? state.file.uploadedFiles : [],
    isAuthenticated:!!state?.connection?.authenticated || !!state?.connection?.loadCompleted,
    localChatHistory: Array.isArray(state?.chat?.localMessages) ? state.chat.localMessages : [],
    polishTasks: Array.isArray(state?.event?.aiTasks)
      ? state.event.aiTasks.filter((t: IAiTask) => t.taskType === AI_TASK_TYPE.MESSAGE_POLISH)
      : [],
  };
}

const mapDispatchToProps = {
  uploadFile: FileActions.requestChatFileUploadSlot,
  uploadedFileUsed: FileActions.uploadedFileUsed,
  sendChatMessage: ChatActions.createChatMessage,
  sendChatMessageToxicChecked: ChatActions.createChatMessageToxicChecked,
  chatCheckIn: ChatActions.chatCheckIn,
  locallySaveChatMessage: ChatActions.localSaveChatMessage,
  displayErrorMessage,
  aiTask: EventActions.aiTask,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ChatStyle)(ChatBottomBar));



// WEBPACK FOOTER //
// ./src/components/chat/ChatBottomBar.tsx